import capitalize from 'lodash/capitalize';
import { FC } from 'react';
import { useTranslation } from 'utils/i18n';
import useCounter from '../../hooks/useCounter/index';
import InputError from '../fields/inputError';

interface Props {
  label: string;
  id: string;
  initialValue: number;
  minValue: number;
  maxValue?: number;
  onChange?: (value: number) => void;
}

const IncrementalInput: FC<Props> = ({
  label,
  id,
  initialValue = 0,
  minValue = 0,
  maxValue,
  onChange = () => {},
}) => {
  const { t } = useTranslation('common');
  const { value, increment, decrement, setTo } = useCounter(initialValue);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: strValue } = event.currentTarget;
    const newValue = parseInt(strValue, 10);

    if (Number.isInteger(newValue)) {
      setTo(newValue);
      onChange(newValue);
    } else {
      setTo(null);
      onChange(0);
    }
  };

  const maxLength = () =>
    Math.max(minValue.toString().length, maxValue!.toString().length);

  const errorMessage = maxValue
    ? t('errors.messages.between', { min: minValue, max: maxValue })
    : t('errors.messages.greater_than', { count: minValue });

  return (
    <>
      <div className="filter__increment">
        <label htmlFor={id}>{label}</label>
        <button
          data-testid="minusBtn"
          onClick={() => {
            decrement();
            onChange(Number(value) - 1);
          }}
          disabled={Number(value) <= minValue}
        >
          -
        </button>
        <input
          data-testid="incrementInput"
          id={id}
          value={value === null ? '' : value}
          onChange={handleValueChange}
          {...(maxValue ? { maxLength: maxLength() } : {})}
        />
        <button
          data-testid="plusBtn"
          data-cy="plusBtn"
          onClick={() => {
            increment();
            onChange(Number(value) + 1);
          }}
          disabled={!!maxValue && Number(value) >= maxValue}
        >
          +
        </button>
      </div>

      {value !== 0 &&
        (Number(value) < minValue ||
          (maxValue && Number(value) > maxValue)) && (
          <InputError errorMsg={capitalize(errorMessage)} />
        )}
    </>
  );
};

export default IncrementalInput;
